import { useEffect, useRef, useState } from "react";
import { AuthorManuscriptNotice } from "@/components/marketplace/AuthorManuscriptNotice";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Clock, DollarSign, MessageCircle, Wallet, CheckCircle2, X, Coffee, FileText, Upload, Download, Trash2, Lock, Unlock, Check, ExternalLink, ShieldCheck } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { StatusBadge } from "@/pages/JobsList";
import { ReviewDialog } from "@/components/marketplace/ReviewDialog";
import { CoffeeChatDialog } from "@/components/marketplace/CoffeeChatDialog";
import { DemoCoffeeChatModal } from "@/components/marketplace/DemoCoffeeChatModal";
import { DemoApplicationModal } from "@/components/marketplace/DemoApplicationModal";
import { PlatformFeeNotice } from "@/components/marketplace/PlatformFeeNotice";
import { QuestionnairePanel } from "@/components/marketplace/QuestionnairePanel";

export default function JobDetail() {
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const messageInFlight = useRef(false);
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [myApp, setMyApp] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  // Bumped whenever the author submits a per-reader review, so the
  // ConfidentialityStatusPanel re-fetches and can flip "Review X" →
  // "✓ Review submitted" without a page reload.
  const [engagementPanelKey, setEngagementPanelKey] = useState(0);
  const [coffeeFor, setCoffeeFor] = useState(null);
  const [demoCoffeeOpen, setDemoCoffeeOpen] = useState(false);
  const [demoApplicationOpen, setDemoApplicationOpen] = useState(false);
  const [funding, setFunding] = useState(false);
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [reopenConfirm, setReopenConfirm] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [visibilityDialog, setVisibilityDialog] = useState({ open: false, choice: null, saving: false });
  // Whether the current user has already submitted their side of the
  // review for this project. Sourced from GET /reviews/mine (given list)
  // — always visible to the review's author. Used to swap the "Write
  // your review" CTA for a confirmation state after submission so
  // re-clicking a fresh empty form doesn't look like the review failed
  // to save.
  const [myReviewForJob, setMyReviewForJob] = useState(null);
  // When GET /api/jobs/{id} fails (project deleted, hidden, or the caller
  // lost access), we used to navigate straight back to /jobs which felt like
  // an "off shelf" broken link — old emails / notifications would appear
  // dead. Instead we surface a friendly in-place card so the URL survives.
  const [loadError, setLoadError] = useState(null); // { status, message }

  const isAuthor = job && user?.id === job.author_id;
  const isReader = user?.roles?.includes("beta_reader");
  const isAdmin = user?.roles?.includes("admin");
  // Multi-reader aware: `job.escrow.accepted_reader_id` only pins the
  // FIRST accepted reader (legacy single-reader proxy). Additional
  // accepted readers are tracked solely on their own `applications` row,
  // which we already load into `myApp` via `/jobs/applications/mine/list`.
  // Falling back to that row lets every accepted reader see the
  // confidentiality panel and manuscript card, not just the first one.
  const isAcceptedReader =
    (job?.escrow?.accepted_reader_id && job.escrow.accepted_reader_id === user?.id) ||
    myApp?.status === "accepted";

  // Deep-link handler: `?review=1` (e.g. from the Reviews page's locked
  // stub card) auto-opens the review dialog for the correct counterpart
  // as soon as the job payload lands. The query param is cleared after
  // opening so a subsequent cancel/refresh doesn't relaunch the dialog.
  useEffect(() => {
    if (!job || !user) return;
    if (search.get("review") !== "1") return;
    // Phase 1: readers can review once THEIR engagement completes.
    // Authors deep-link into the per-reader review from the notification
    // (which carries entity.reader_id) — that flow is handled by the
    // engagement panel, not this hook.
    const isAuthorHere = user.id === job.author_id;
    if (isAuthorHere) return;
    const readerEngagementDone = myApp?.engagement?.state === "completed";
    const legacy = job.status === "completed" && job.escrow?.accepted_reader_id === user.id;
    if (!readerEngagementDone && !legacy) return;
    setReviewFor(job.author_id);
    const next = new URLSearchParams(search);
    next.delete("review");
    setSearch(next, { replace: true });
  }, [job, user, search, setSearch, myApp]);

  // Fetch the current user's own review for this project (if any) so we
  // can flip the "Leave a review" card into a confirmation state after
  // submission. `given` on /reviews/mine is always fully visible to the
  // author of the review — no double-blind concern here.
  const refreshMyReview = async () => {
    if (!job || !user) return;
    const isAuthorHere = user.id === job.author_id;
    if (isAuthorHere) {
      // Author's per-reader review status lives in the engagement panel,
      // not on the top-level card — skip the /reviews/mine fetch here.
      setMyReviewForJob(null);
      return;
    }
    const readerEngagementDone = myApp?.engagement?.state === "completed";
    const legacy = job.status === "completed" && job.escrow?.accepted_reader_id === user.id;
    if (!readerEngagementDone && !legacy) {
      setMyReviewForJob(null);
      return;
    }
    try {
      const { data } = await api.get("/reviews/mine");
      const mine = (data?.given || []).find((r) => r.job_id === id) || null;
      setMyReviewForJob(mine);
    } catch { /* silent — card falls back to the write-review CTA */ }
  };
  useEffect(() => {
    refreshMyReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, user, myApp]);

  const load = async () => {
    try {
      setLoadError(null);
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      if (user?.id === data.author_id) {
        const { data: apps } = await api.get(`/jobs/${id}/applications`);
        setApplications(apps);
      } else if (isReader) {
        const { data: mine } = await api.get(`/jobs/applications/mine/list`);
        const found = mine.find((m) => m.job?.id === id);
        setMyApp(found?.application || null);
      }
    } catch (e) {
      // Distinguish "unavailable / hidden" (404/403) from a real network
      // failure. Only actual network / server errors get a toast; a 404 or
      // 403 is expected when following a stale link and rendered inline.
      const status = e?.response?.status;
      if (status === 404 || status === 403) {
        setLoadError({ status, message: formatApiError(e) });
      } else {
        toast.error(formatApiError(e));
        setLoadError({ status: status || 0, message: formatApiError(e) });
      }
      setJob(null);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user?.id]);

  // Poll payment status after returning from Stripe
  const stripeSid = search.get("stripe_session");
  useEffect(() => {
    if (!stripeSid) return;
    setPaymentPolling(true);
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${stripeSid}`);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          toast.success("Escrow funded — thank you!");
          setPaymentPolling(false);
          search.delete("stripe_session");
          setSearch(search, { replace: true });
          load();
        } else if (data.status === "expired" || attempts >= 8) {
          clearInterval(iv);
          setPaymentPolling(false);
          if (data.status === "expired") toast.error("Payment session expired.");
          else toast.message("Still processing — refresh in a moment to see the update.");
          search.delete("stripe_session");
          setSearch(search, { replace: true });
        }
      } catch {
        clearInterval(iv);
        setPaymentPolling(false);
      }
    }, 2500);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [stripeSid]);

  if (!job) {
    if (loadError) {
      return (
        <SiteShell>
          <section className="container-page py-16" data-testid="job-detail-unavailable">
            <Link to="/jobs" className="text-sm text-muted-foreground hover:underline">← Back to marketplace</Link>
            <div className="mt-6 max-w-xl mx-auto card-soft text-center">
              <h1 className="font-serif text-3xl">This project isn&rsquo;t available to you</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                It may have been completed, closed, made private, or removed by
                the author. If you were previously accepted on this project,
                sign in with that account to see it.
              </p>
              {!user && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Not signed in? <Link to={`/login?next=${encodeURIComponent(`/jobs/${id}`)}`} className="link-underline text-[hsl(var(--sb-forest-2))]">Sign in</Link> to check.
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                <Link to="/jobs" className="btn-primary" data-testid="job-detail-unavailable-back">
                  Back to marketplace
                </Link>
                {user && (
                  <Link to="/dashboard" className="btn-ghost border" data-testid="job-detail-unavailable-dashboard">
                    Open dashboard
                  </Link>
                )}
              </div>
            </div>
          </section>
        </SiteShell>
      );
    }
    return <SiteShell><div className="container-page py-16">Loading…</div></SiteShell>;
  }

  const fund = async () => {
    setFunding(true);
    try {
      const { data } = await api.post(`/jobs/${id}/escrow/checkout`, { origin_url: window.location.origin });
      // hand off to Stripe
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setFunding(false);
    }
  };

  const complete = async () => {
    try { const { data } = await api.post(`/jobs/${id}/complete`); setJob(data); toast.success("Project marked complete."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this project?")) return;
    try { const { data } = await api.post(`/jobs/${id}/cancel`); setJob(data); toast.success("Project cancelled"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reopen = async () => {
    setReopening(true);
    try {
      const { data } = await api.post(`/jobs/${id}/reopen`);
      setJob(data);
      setReopenConfirm(false);
      toast.success("Project reopened");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReopening(false);
    }
  };
  const acceptApp = async (appId) => {
    try {
      const { data } = await api.post(`/jobs/applications/${appId}/accept`);
      setJob(data.job);
      // Only replace the accepted application's row — leave every other
      // application exactly as the backend has it. Marking siblings as
      // "declined" locally was legacy single-reader behavior; the backend
      // never touches sibling applications on accept, and authors may
      // accept multiple readers per project.
      setApplications((a) => a.map((x) => x.id === appId ? data.application : x));
      toast.success("Application accepted");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const declineApp = async (appId) => {
    try {
      const { data } = await api.post(`/jobs/applications/${appId}/decline`);
      setApplications((a) => a.map((x) => x.id === appId ? data : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const withdraw = async () => {
    try { const { data } = await api.post(`/jobs/applications/${myApp.id}/withdraw`); setMyApp(data); toast.success("Application withdrawn"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const messageParticipant = async (otherUserId) => {
    if (messageInFlight.current) return; // ignore rapid repeat taps
    messageInFlight.current = true;
    try {
      const { data } = await api.post(`/threads`, { other_user_id: otherUserId, job_id: id });
      navigate(`/messages/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { messageInFlight.current = false; }
  };
  const toggleVisibility = async (nextVisibility) => {
    try {
      const { data } = await api.patch(`/jobs/${id}/admin/visibility`, { visibility: nextVisibility });
      setJob(data);
      const msgByVal = {
        default: "Set to Private Draft — hidden from beta readers.",
        public: "Set to Public — visible to all eligible beta readers.",
        private_testers: "Set to Early Access Members Only — only Early Access members see this project.",
      };
      toast.success(msgByVal[nextVisibility] || "Visibility updated.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmVisibilityChange = async () => {
    const choice = visibilityDialog.choice;
    if (!choice) return;
    setVisibilityDialog((s) => ({ ...s, saving: true }));
    try {
      const { data } = await api.patch(`/jobs/${id}/admin/visibility`, { visibility: choice });
      setJob(data);
      const msgByVal = {
        default: "Set to Private Draft — hidden from beta readers.",
        public: "Now Public — beta readers can view and apply.",
        private_testers: "Set to Early Access Members Only — approved Early Access members can view and apply.",
      };
      toast.success(msgByVal[choice] || "Visibility updated.");
      setVisibilityDialog({ open: false, choice: null, saving: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setVisibilityDialog((s) => ({ ...s, saving: false }));
    }
  };

  const closeApplications = async () => {
    if (!window.confirm("Close applications to new readers?\n\nAlready-accepted readers keep full access. You can reopen anytime.")) return;
    try {
      const { data } = await api.post(`/jobs/${id}/close-applications`);
      setJob(data);
      toast.success("Applications closed. Accepted readers are unaffected.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reopenApplications = async () => {
    try {
      const { data } = await api.post(`/jobs/${id}/reopen-applications`);
      setJob(data);
      toast.success("Applications reopened.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveProject = async () => {
    if (!window.confirm(
      "Archive this project?\n\n" +
      "It will be hidden from Discover and other users. Applications, " +
      "Coffee Chats, reviews, and manuscript history are kept intact. " +
      "You can restore it anytime from your archived projects."
    )) return;
    try {
      const { data } = await api.post(`/jobs/${id}/archive`);
      setJob(data);
      toast.success("Project archived.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unarchiveProject = async () => {
    try {
      const { data } = await api.post(`/jobs/${id}/unarchive`);
      setJob(data);
      toast.success("Project restored. It's visible again under its previous state.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const escrow = job.escrow || {};
  const isDemoListing = job.listing_type === "demo";
  const showFund = isAuthor && job.status === "in_progress" && escrow.state !== "funded" && escrow.state !== "released";
  // Phase 1: project-level "Mark project complete" is intentionally hidden.
  // Authors complete each accepted reader individually via the
  // ConfidentialityStatusPanel below. The endpoint stays available for
  // legacy compatibility but the UI never surfaces a project-wide action.
  const showComplete = false;
  // Reader-side review card only. When the caller is the accepted reader
  // AND their own engagement is `completed`, they see the "Leave a review"
  // card. Authors review individual readers from the engagement panel
  // (multiple readers → multiple reviews), never from a project-level card.
  const myEngagementCompleted = myApp?.engagement?.state === "completed";
  const canReview = !isAuthor && isReader && (
    myEngagementCompleted ||
    // Legacy compatibility for projects that completed under the old
    // single-escrow flow before Phase 1 shipped.
    (job.status === "completed" && isAcceptedReader)
  );

  return (
    <SiteShell>
      <section className="container-page py-10 md:py-14" data-testid={TID.jobs.detailRoot}>
        {job.archived && (isAuthor || isAdmin) && (
          <div
            className="mb-6 rounded-xl border-2 border-[hsl(var(--sb-clay))] bg-[hsl(var(--sb-clay))]/15 dark:bg-[hsl(var(--sb-clay))]/25 px-4 py-3 flex items-start gap-3"
            data-testid="job-archived-banner"
          >
            <div className="shrink-0 mt-0.5 rounded-full bg-[hsl(var(--sb-clay))] text-[hsl(var(--sb-cream))] w-7 h-7 grid place-items-center font-bold text-xs">
              ARC
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[hsl(var(--sb-ink))] dark:text-[hsl(var(--sb-cream))]">
                This project is archived
              </div>
              <p className="text-xs mt-0.5 text-[hsl(var(--sb-ink))]/85 dark:text-[hsl(var(--sb-cream))]/85">
                It&apos;s hidden from Discover and other users. All applications,
                Coffee Chats, reviews, and manuscript history are intact.
                Restore it from the sidebar to make it discoverable again.
                {job.archived_at && (
                  <> Archived {new Date(job.archived_at).toLocaleDateString()}.</>
                )}
              </p>
            </div>
          </div>
        )}
        <Link to="/jobs" className="text-sm text-muted-foreground hover:underline">← Back to marketplace</Link>

        <div className="mt-6 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-soft">
              {job.cover_url && (
                <div
                  className="mb-5 -mt-1 rounded-lg overflow-hidden border border-border/60 bg-muted"
                  style={{
                    aspectRatio: "2 / 3",
                    maxWidth: "180px",
                    backgroundImage: `url(${job.cover_url})`,
                    backgroundSize: `${job.cover_position?.zoom ?? 100}% ${job.cover_position?.zoom ?? 100}%`,
                    backgroundPosition: `${job.cover_position?.x ?? 50}% ${job.cover_position?.y ?? 50}%`,
                    backgroundRepeat: "no-repeat",
                  }}
                  role="img"
                  aria-label={`Book cover for ${job.title || "this project"}`}
                  data-testid="job-detail-cover"
                />
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="chip">{job.genre}</span>
                <StatusBadge status={job.status} />
                <span className="text-xs text-muted-foreground">by {job.author_name}</span>
                {isDemoListing && (
                  <span
                    className="chip !bg-[hsl(var(--sb-clay))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-clay))] font-semibold"
                    data-testid="job-listing-type-demo-badge"
                    title="This is a demo or example listing — it does not accept applications, Coffee Chats, or payment arrangements."
                  >
                    Demo / Test project
                  </span>
                )}
                {(job.visibility === "public" || (!job.visibility && job.is_demo)) && (
                  <span className="chip !bg-[hsl(var(--sb-forest))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-forest))] font-semibold" data-testid="job-demo-badge">
                    Public — visible to all testers
                  </span>
                )}
                {job.visibility === "private_testers" && (
                  <span className="chip !bg-[hsl(var(--sb-clay))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-clay))] font-semibold" data-testid="job-private-testers-badge">
                    Early Access members only
                  </span>
                )}
                {(job.visibility === "default" || (!job.visibility && !job.is_demo)) && (
                  <span className="chip !bg-[hsl(var(--sb-gold))] !text-[hsl(var(--sb-ink))] !border-[hsl(var(--sb-gold))] font-semibold" data-testid="job-private-draft-badge">
                    Private Draft
                  </span>
                )}
              </div>
              {(job.visibility === "default" || (!job.visibility && !job.is_demo)) && (
                <div className="mt-3 rounded-lg lantern-notice px-4 py-3 text-sm" data-testid="job-private-draft-warning">
                  <div className="lantern-notice-heading text-xs mb-1">Private draft — not visible to beta readers</div>
                  <p className="leading-relaxed">
                    This project is private. Beta readers cannot see it or apply. To begin receiving applications, change the visibility to <em>Public</em> or <em>Early Access Members Only</em>.
                    {(job.applications_count || 0) > 0 && (
                      <><br /><span className="lantern-muted text-xs">Existing applications ({job.applications_count}) are retained for recordkeeping.</span></>
                    )}
                  </p>
                  {(isAuthor || isAdmin) && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setVisibilityDialog({ open: true, choice: null, saving: false })}
                        data-testid="job-change-visibility-btn"
                        className="inline-flex items-center gap-2 rounded-md border border-[hsl(var(--sb-gold))]/70 bg-[hsl(var(--sb-gold))]/15 px-3 py-1.5 text-xs font-semibold text-[hsl(var(--sb-gold-2))] dark:text-[hsl(var(--sb-gold))] hover:bg-[hsl(var(--sb-gold))]/25 transition-colors"
                      >
                        Change Visibility
                      </button>
                      <span className="ml-3 text-[11px] lantern-muted">You'll be asked to confirm before publishing.</span>
                    </div>
                  )}
                </div>
              )}
              {isAdmin && (
                <div className="mt-3 rounded-lg border border-dashed border-[hsl(var(--sb-forest))]/30 bg-[hsl(var(--sb-moss))]/30 px-3 py-3 text-xs" data-testid="job-admin-visibility-card">
                  <div className="font-medium text-[hsl(var(--sb-forest-2))] mb-1">Admin visibility</div>
                  <p className="text-muted-foreground mb-2">
                    Choose who can see and apply to this project.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {[
                      { value: "default", label: "Private Draft", hint: "Only the author and administrators can view it." },
                      { value: "public", label: "Public", hint: "All eligible beta readers can view and apply." },
                      { value: "private_testers", label: "Early Access Members Only", hint: "Approved Early Access members and administrators can view and apply." },
                    ].map((opt) => {
                      const active = (job.visibility || (job.is_demo ? "public" : "default")) === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => !active && toggleVisibility(opt.value)}
                          data-testid={`job-visibility-${opt.value}`}
                          className={`text-left rounded-md border p-2.5 transition-colors ${
                            active
                              ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)]"
                              : "border-border bg-[hsl(var(--card))] !text-[hsl(var(--sb-ink))] hover:bg-[hsl(var(--sb-cream-2))]"
                          }`}
                        >
                          <div className={`text-[11px] font-semibold ${active ? "" : "text-[hsl(var(--sb-ink))]"}`}>{opt.label}</div>
                          <div className={`text-[10px] mt-0.5 ${active ? "opacity-80" : "!text-[hsl(var(--sb-ink))]/85"}`}>{opt.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {isAdmin && (
                <div className="mt-3 rounded-lg border border-dashed border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/5 px-3 py-3 text-xs" data-testid="job-admin-listing-type-card">
                  <div className="font-medium text-[hsl(var(--sb-clay))] mb-1">Admin: listing type</div>
                  <p className="text-muted-foreground mb-2">
                    Reclassify this project between Real Opportunity and Demo/Test. Changes apply immediately — including who can see and apply.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { value: "real", label: "Real Opportunity", hint: "Accepts applications + Coffee Chats" },
                      { value: "demo", label: "Demo / Test Project", hint: "No applications, no Coffee Chats" },
                    ].map((opt) => {
                      const active = (job.listing_type || "real") === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={async () => {
                            if (active) return;
                            try {
                              const { data } = await api.patch(`/jobs/${id}`, { listing_type: opt.value });
                              setJob(data);
                              toast.success(
                                opt.value === "demo"
                                  ? "Marked as Demo/Test — applications & Coffee Chats disabled."
                                  : "Marked as Real Opportunity — applications & Coffee Chats enabled."
                              );
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          data-testid={`job-listing-type-${opt.value}`}
                          className={`text-left rounded-md border p-2.5 transition-colors ${
                            active
                              ? "border-[hsl(var(--sb-clay))] bg-[hsl(var(--sb-clay))] text-[hsl(40_60%_96%)]"
                              : "border-border bg-[hsl(var(--card))] !text-[hsl(var(--sb-ink))] hover:bg-[hsl(var(--sb-cream-2))]"
                          }`}
                        >
                          <div className={`text-[11px] font-semibold ${active ? "" : "text-[hsl(var(--sb-ink))]"}`}>{opt.label}</div>
                          <div className={`text-[10px] mt-0.5 ${active ? "opacity-80" : "!text-[hsl(var(--sb-ink))]/85"}`}>{opt.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <h1 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight">{job.title}</h1>
              {!isAuthor && isReader && !isDemoListing && job.status === "open" && !job.applications_closed && !myApp && (
                <div className="mt-4" data-testid="jobs-apply-open-top-wrap">
                  <button
                    type="button"
                    onClick={() => setShowApply(true)}
                    data-testid="jobs-apply-open-top"
                    className="btn-primary"
                  >
                    Apply to this project
                  </button>
                </div>
              )}
              <p className="mt-4 whitespace-pre-line text-foreground/85 leading-relaxed">{job.description}</p>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={BookOpen} label="Word count" value={job.word_count?.toLocaleString()} />
                <Stat icon={DollarSign} label="Budget" value={formatCompensation(job)} />
                <Stat icon={Clock} label="Turnaround" value={`${job.turnaround_days} days`} />
                <Stat icon={CheckCircle2} label="Style" value={job.feedback_style?.replace("_", " ")} />
              </div>
              {job.tags?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {job.tags.map((t) => <span key={t} className="chip">{t}</span>)}
                </div>
              )}
            </div>

            {isAuthor && applications.length > 0 && (
              <div className="card-soft" data-testid={TID.jobs.applicationsList}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-serif text-2xl">Applications</h2>
                    <p className="text-sm text-muted-foreground mt-1">You can accept multiple beta readers for this project. Each application is handled separately — accept or decline them individually. The project stays open for new applications until you close or mark it filled.</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {(() => {
                      const accepted = applications.filter((a) => a.status === "accepted").length;
                      return accepted > 0 ? (
                        <span
                          className="chip-success inline-flex items-center gap-1.5"
                          data-testid="job-accepted-readers-chip"
                          title="Number of beta readers you've accepted"
                        >
                          <Check size={12} /> Accepted readers ({accepted})
                        </span>
                      ) : null;
                    })()}
                    {!isDemoListing && (
                      job.applications_closed ? (
                        (job.status === "completed" || job.status === "cancelled") ? (
                          <div
                            className="inline-flex flex-col items-end gap-1"
                            data-testid="job-reopen-applications-disabled"
                          >
                            <button
                              type="button"
                              disabled
                              aria-disabled="true"
                              title={`This ${job.status === "cancelled" ? "cancelled" : "completed"} project no longer accepts applications. Use "Reopen project" to bring it back first.`}
                              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border border-border bg-muted text-foreground/60 cursor-not-allowed"
                            >
                              <Unlock size={13} /> Reopen applications
                            </button>
                            <span className="text-[11px] text-muted-foreground max-w-[220px] text-right">
                              Project is {job.status}. Reopen the project first.
                            </span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={reopenApplications}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border border-[hsl(var(--sb-forest-2))] bg-[hsl(var(--sb-forest-2))]/12 text-[hsl(var(--sb-forest-2))] dark:text-[hsl(var(--sb-gold))] hover:bg-[hsl(var(--sb-forest-2))]/25 transition-colors"
                            data-testid="job-reopen-applications"
                          >
                            <Unlock size={13} /> Reopen applications
                          </button>
                        )
                      ) : (
                        <button
                          type="button"
                          onClick={closeApplications}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border border-[hsl(var(--sb-clay))] bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay))] dark:text-[hsl(var(--sb-gold))] hover:bg-[hsl(var(--sb-clay))]/20 transition-colors"
                          data-testid="job-close-applications"
                        >
                          <Lock size={13} /> Close applications
                        </button>
                      )
                    )}
                  </div>
                </div>
                {job.applications_closed && (
                  <div className="mt-3 text-xs rounded-lg border border-[hsl(var(--sb-clay))]/30 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay-2))] px-3 py-2" data-testid="job-applications-closed-notice">
                    New applications are paused. Already-accepted readers keep full access to messages and the manuscript.
                  </div>
                )}
                {!isDemoListing && (
                  <div className="mt-4">
                    <PlatformFeeNotice variant="real" />
                  </div>
                )}
                <div className="mt-6 space-y-4">
                  {applications.map((a) => (
                    <ApplicationRow key={a.id} app={a} jobStatus={job.status}
                      onAccept={() => acceptApp(a.id)}
                      onDecline={() => declineApp(a.id)}
                      onMessage={() => messageParticipant(a.reader_id)} />
                  ))}
                </div>
              </div>
            )}

            {isDemoListing && !isAuthor && (
              <div
                className="card-soft border-2 border-[hsl(var(--sb-clay))]/60 bg-[hsl(var(--sb-cream))] shadow-md"
                data-testid="job-demo-notice"
              >
                <h2 className="font-serif text-2xl text-[hsl(var(--sb-forest-2))]">Demo / Test Project</h2>
                <p className="mt-2 text-sm md:text-base text-foreground/85 leading-relaxed font-medium">
                  This listing exists to demonstrate how the marketplace works. It does not accept real applications, Coffee Chat requests, or payment arrangements.
                </p>
                {!isAdmin && (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="job-demo-cta-block">
                    <button
                      type="button"
                      onClick={() => setDemoCoffeeOpen(true)}
                      title="Practice interaction — no real user is contacted."
                      data-testid="job-try-demo-coffee-btn"
                      className="rounded-full bg-[hsl(var(--sb-clay))] hover:bg-[hsl(var(--sb-clay-2))] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1.5 shadow-sm border-2 border-[hsl(var(--sb-clay-2))]"
                    >
                      <Coffee size={12} /> Try Demo Coffee Chat
                    </button>
                    <button
                      type="button"
                      onClick={() => setDemoApplicationOpen(true)}
                      title="Practice interaction — no real user receives it."
                      data-testid="job-try-demo-application-btn"
                      className="rounded-full border-2 border-[hsl(var(--sb-clay-2))] bg-[hsl(var(--sb-clay))]/20 hover:bg-[hsl(var(--sb-clay))]/30 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[hsl(var(--sb-clay-2))] inline-flex items-center gap-1.5 shadow-sm"
                    >
                      Try Demo Application
                    </button>
                  </div>
                )}
              </div>
            )}

            {!isAuthor && isReader && !isDemoListing && (
              <ReaderPanel job={job} myApp={myApp} onApply={() => setShowApply(true)}
                onEdit={() => setEditApp(myApp)}
                onWithdraw={withdraw} onMessage={() => messageParticipant(job.author_id)} />
            )}

            {!isAuthor && !isReader && job.status === "open" && !isDemoListing && (
              <ApplyGuideCard signedIn={!!user} />
            )}

            <ManuscriptCard job={job} isAuthor={isAuthor} isAcceptedReader={isAcceptedReader} onChanged={setJob} onOpenReview={setReviewFor} engagementPanelKey={engagementPanelKey} />

            {(isAuthor || isAcceptedReader) && (job.feedback_style === "questionnaire" || job.feedback_style === "both") && (
              <QuestionnairePanel
                job={job}
                isAuthor={isAuthor}
                isAcceptedReader={isAcceptedReader}
                onJobUpdated={setJob}
              />
            )}

            {canReview && (
              <div className="card-soft" data-testid={TID.reviews.root}>
                <h2 className="font-serif text-2xl">
                  {myReviewForJob ? "Your review is in" : "Leave a review"}
                </h2>
                {myReviewForJob ? (
                  <>
                    <p className="text-sm text-muted-foreground mt-1">
                      Submitted {new Date(myReviewForJob.created_at).toLocaleDateString()}. It&apos;ll unlock publicly once the other side submits — or automatically after 14 days.
                    </p>
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border-2 border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))] text-[hsl(var(--sb-cream))] px-3 py-1 text-xs font-bold shadow-sm"
                        data-testid="job-review-submitted-chip"
                      >
                        <Check size={14} strokeWidth={2.75} /> You gave {myReviewForJob.average?.toFixed(2)} / 5
                      </span>
                      <Link to="/reviews" className="text-sm link-underline">
                        See it on your Reviews page →
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mt-1">
                      Reviews are only visible after both parties have submitted (or after 14 days).
                    </p>
                    <button className="btn-primary mt-4" data-testid={TID.reviews.open}
                      onClick={() => setReviewFor(isAuthor ? escrow.accepted_reader_id : job.author_id)}>
                      Write your review
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="card-soft" data-testid="job-payments-notice">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2">
                <Wallet size={14} /> Payments
              </div>
              <div className="mt-3 font-serif text-2xl leading-tight">
                Payments not currently active
              </div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Secure in-platform payments and escrow are planned for a future update. Payments are not currently processed by Last Bookmark.
              </p>
              <div className="mt-4 space-y-2">
                {isAuthor && job.status !== "completed" && job.status !== "cancelled" && (
                  <Link
                    to={`/jobs/${id}/edit`}
                    data-testid="jobs-detail-edit"
                    className="btn-ghost border w-full text-center"
                  >
                    Edit project
                  </Link>
                )}
                {isAuthor && job.status !== "completed" && job.status !== "cancelled" && (
                  <button onClick={cancel} data-testid={TID.jobs.detailCancel} className="btn-ghost border w-full text-destructive">Cancel project</button>
                )}
                {(isAuthor || isAdmin) && !job.archived && (
                  <button
                    onClick={archiveProject}
                    data-testid="job-archive-btn"
                    className="btn-ghost border w-full"
                    title="Hide this project from Discover without deleting anything"
                  >
                    Archive project
                  </button>
                )}
                {(isAuthor || isAdmin) && job.archived && (
                  <button
                    onClick={unarchiveProject}
                    data-testid="job-unarchive-btn"
                    className="btn-primary w-full"
                  >
                    Restore project
                  </button>
                )}
                {isAuthor && (job.status === "cancelled" || job.status === "completed") && (
                  <button
                    onClick={() => setReopenConfirm(true)}
                    data-testid="jobs-detail-reopen"
                    className="btn-ghost border w-full"
                  >
                    Reopen project
                  </button>
                )}
              </div>
            </div>

            {(isAuthor || isReader) && !isAuthor && !isDemoListing && (
              <button className="btn-ghost border w-full" data-testid={TID.coffeeChats.invite}
                onClick={() => setCoffeeFor(job.author_id)}>
                <Coffee size={16} /> Request coffee chat
              </button>
            )}
            {isAuthor && job.escrow?.accepted_reader_id && !isDemoListing && (
              <button className="btn-ghost border w-full" data-testid={TID.coffeeChats.invite}
                onClick={() => setCoffeeFor(job.escrow.accepted_reader_id)}>
                <Coffee size={16} /> Invite to coffee chat
              </button>
            )}

            {isAuthor && job.status === "in_progress" && escrow.accepted_reader_id && (
              <button className="btn-primary w-full" data-testid={TID.jobs.messageParticipant}
                onClick={() => messageParticipant(escrow.accepted_reader_id)}>
                <MessageCircle size={16} /> Message your reader
              </button>
            )}
          </aside>
        </div>

        {showApply && <ApplyDialog jobId={id} unpaid={Number(job.budget_min ?? 0) === 0 && Number(job.budget_max ?? 0) === 0} onClose={() => setShowApply(false)} onDone={() => { setShowApply(false); load(); }} />}
        {editApp && (
          <ApplyDialog
            jobId={id}
            unpaid={Number(job.budget_min ?? 0) === 0 && Number(job.budget_max ?? 0) === 0}
            existing={editApp}
            onClose={() => setEditApp(null)}
            onDone={() => { setEditApp(null); load(); }}
          />
        )}
        {reviewFor && <ReviewDialog jobId={id} subjectId={reviewFor} onClose={() => setReviewFor(null)} onDone={() => { setReviewFor(null); refreshMyReview(); setEngagementPanelKey((k) => k + 1); }} />}
        {coffeeFor && <CoffeeChatDialog otherUserId={coffeeFor} jobId={id} onClose={() => setCoffeeFor(null)} onDone={() => { setCoffeeFor(null); toast.success("Coffee chat requested"); }} />}
        <DemoCoffeeChatModal
          open={demoCoffeeOpen}
          onClose={() => setDemoCoffeeOpen(false)}
          context={{
            targetName: job.author_name || "Demo Author",
            sourceLabel: `${job.author_name || "Demo Author"} · ${job.title}`,
          }}
        />
        <DemoApplicationModal
          open={demoApplicationOpen}
          onClose={() => setDemoApplicationOpen(false)}
          context={{
            targetName: job.author_name || "Demo Author",
            projectTitle: job.title || "Demo Project",
            unpaid: Number(job.budget_min ?? 0) === 0 && Number(job.budget_max ?? 0) === 0,
          }}
        />
        {visibilityDialog.open && (
          <VisibilityConfirmModal
            currentVisibility={job.visibility || (job.is_demo ? "public" : "default")}
            choice={visibilityDialog.choice}
            saving={visibilityDialog.saving}
            onChoose={(val) => setVisibilityDialog((s) => ({ ...s, choice: val }))}
            onCancel={() => !visibilityDialog.saving && setVisibilityDialog({ open: false, choice: null, saving: false })}
            onConfirm={confirmVisibilityChange}
          />
        )}
        {reopenConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" data-testid="jobs-detail-reopen-modal">
            <div className="w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-2xl">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Confirm</div>
              <div className="mt-1 text-lg font-serif">Reopen this project?</div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                This will restore the project to its previous active state so beta readers can find and apply to it again. Existing applications, coffee chats, and messages are unchanged.
              </p>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  className="btn-ghost border"
                  onClick={() => !reopening && setReopenConfirm(false)}
                  disabled={reopening}
                  data-testid="jobs-detail-reopen-cancel"
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={reopen}
                  disabled={reopening}
                  data-testid="jobs-detail-reopen-confirm"
                >
                  {reopening ? "Reopening…" : "Reopen project"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </SiteShell>
  );
}

function ConfidentialityStatusPanel({ jobId, isAuthor, refreshKey, onEngagementCompleted, onOpenReview }) {
  const [rows, setRows] = useState(null);
  const [completing, setCompleting] = useState(null);
  // Phase B.2: author "View agreement" modal — small read-only viewer so
  // the author can refer back to the exact wording readers were asked to
  // accept. Agreement text is fetched once from /jobs/{id}/confidentiality
  // (author/admin bypass path), which also returns the current canonical
  // text + version.
  const [agreement, setAgreement] = useState(null); // { version, text } | null
  const [viewAgreementOpen, setViewAgreementOpen] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/confidentiality/readers`);
      setRows(data.results || []);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}/confidentiality/readers`);
        if (alive) setRows(data.results || []);
      } catch {
        if (alive) setRows([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobId, refreshKey]);
  useEffect(() => {
    // Fetch agreement text once — author path returns the canonical text.
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}/confidentiality`);
        if (alive) setAgreement({ version: data.version, text: data.text });
      } catch {
        if (alive) setAgreement(null);
      }
    })();
    return () => { alive = false; };
  }, [jobId]);
  const completeEngagement = async (applicationId, readerName) => {
    if (!window.confirm(
      `Mark ${readerName || "this reader"}'s engagement complete?\n\n` +
      "Only this reader's engagement will change. Other accepted readers " +
      "keep working. You and this reader will then be invited to review " +
      "each other."
    )) return;
    setCompleting(applicationId);
    try {
      const { data } = await api.post(`/jobs/${jobId}/applications/${applicationId}/complete`);
      toast.success(
        data.project_autocompleted
          ? "Engagement complete. All engagements resolved — project auto-completed."
          : "Engagement complete. You can now review each other."
      );
      onEngagementCompleted?.(data);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompleting(null);
    }
  };
  if (rows === null) return null;
  if (rows.length === 0) return null;

  // Panel-level summary — mirrors the admin analytics tally so the author
  // sees "N of M accepted" at a glance.
  const total = rows.length;
  const acceptedCount = rows.filter((r) => r.confidentiality_state === "accepted").length;
  const pendingCount = rows.filter((r) => r.confidentiality_state === "pending").length;
  const declinedCount = rows.filter((r) => r.confidentiality_state === "declined").length;
  const updateCount = rows.filter((r) => r.confidentiality_state === "update_required").length;

  return (
    <div
      className="mt-4 rounded-xl border bg-muted/30 p-3.5"
      data-testid="author-confidentiality-status"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium flex items-center gap-1.5">
          <ShieldCheck size={12} /> Confidentiality &amp; engagement status
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-xs text-foreground/80"
            data-testid="author-confidentiality-summary"
          >
            <strong>{acceptedCount}</strong> of <strong>{total}</strong> can open the manuscript
            {pendingCount > 0 && <> · <strong>{pendingCount}</strong> signature pending</>}
            {updateCount > 0 && <> · <strong>{updateCount}</strong> signed an older version, update needed</>}
            {declinedCount > 0 && <> · <strong>{declinedCount}</strong> declined</>}
            {total - acceptedCount > 0 && <> · <strong>{total - acceptedCount}</strong> locked</>}
          </span>
          {agreement?.text && (
            <button
              type="button"
              onClick={() => setViewAgreementOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--sb-forest-2))]/40 bg-background px-3 py-1.5 text-xs sm:text-sm font-medium text-[hsl(var(--sb-forest-2))] hover:bg-[hsl(var(--sb-forest-2))]/10 min-h-[40px]"
              data-testid="author-confidentiality-view-agreement"
            >
              <FileText size={14} />
              <span>View agreement</span>
            </button>
          )}
        </div>
      </div>
      <ul className="mt-3 space-y-3 text-sm">
        {rows.map((r) => {
          const reader = r.reader || {};
          const readerId = reader.id;
          const name = reader.display_name || reader.email || "Reader";
          const confState = r.confidentiality_state || (r.accepted ? "accepted" : "pending");
          const acceptedAt = r.accepted_at ? new Date(r.accepted_at) : null;
          const pendingSince = r.pending_since ? new Date(r.pending_since) : null;
          const declinedAt = r.declined_at ? new Date(r.declined_at) : null;
          const dlAt = r.manuscript_first_downloaded_at
            ? new Date(r.manuscript_first_downloaded_at)
            : null;
          const engagementState = r.engagement_state || "active";
          const engagementDone = engagementState === "completed";
          const engagementCompletedAt = r.engagement_completed_at
            ? new Date(r.engagement_completed_at)
            : null;
          const applicationId = r.application_id;
          const unlocked = confState === "accepted";

          // Chip classes per state.
          const chipBase =
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium";
          let confChip = null;
          if (confState === "accepted") {
            confChip = (
              <span
                className={`${chipBase} border-emerald-500/60 bg-emerald-100 text-emerald-900`}
                data-testid={`author-conf-chip-accepted-${readerId || name}`}
                data-conf-state="accepted"
              >
                <ShieldCheck size={11} /> Confidentiality complete
                {r.version && <> · v{r.version}</>}
                {acceptedAt && <> · {acceptedAt.toLocaleDateString()}</>}
              </span>
            );
          } else if (confState === "update_required") {
            confChip = (
              <span
                className={`${chipBase} border-amber-500/70 bg-amber-100 text-amber-950`}
                data-testid={`author-conf-chip-update-${readerId || name}`}
                data-conf-state="update_required"
              >
                <Clock size={11} /> Signed v{r.version} — needs v{agreement?.version || "2.0"}
                {acceptedAt && <> · signed {acceptedAt.toLocaleDateString()}</>}
              </span>
            );
          } else if (confState === "declined") {
            confChip = (
              <span
                className={`${chipBase} border-destructive/40 bg-destructive/10 text-destructive`}
                data-testid={`author-conf-chip-declined-${readerId || name}`}
                data-conf-state="declined"
              >
                <X size={11} /> Confidentiality declined
                {declinedAt && <> · {declinedAt.toLocaleDateString()}</>}
              </span>
            );
          } else {
            confChip = (
              <span
                className={`${chipBase} border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay-2))]`}
                data-testid={`author-conf-chip-pending-${readerId || name}`}
                data-conf-state="pending"
              >
                <Clock size={11} /> Signature pending
                {pendingSince && <> · since {pendingSince.toLocaleDateString()}</>}
              </span>
            );
          }
          const accessChip = (
            <span
              className={`${chipBase} ${
                unlocked
                  ? "border-transparent bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)] dark:bg-[hsl(var(--sb-forest))] dark:text-[hsl(40_60%_96%)]"
                  : "border-border bg-muted text-foreground/80"
              }`}
              data-testid={`author-conf-access-${readerId || name}`}
            >
              {unlocked ? <Unlock size={11} /> : <Lock size={11} />}
              {unlocked ? "Manuscript accessible" : "Manuscript locked"}
            </span>
          );
          return (
            <li
              key={readerId || name}
              className="flex flex-col gap-1.5"
              data-testid={`author-confidentiality-row-${readerId || name}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="min-w-0">
                  {readerId ? (
                    <Link
                      to={`/profile/${readerId}`}
                      className="font-medium link-underline"
                    >
                      {name}
                    </Link>
                  ) : (
                    <span className="font-medium">{name}</span>
                  )}
                </span>
                {confChip}
                {accessChip}
                {engagementDone && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))]/45 dark:bg-[hsl(var(--sb-forest))]/55 px-2.5 py-0.5 text-[11px] font-bold text-[hsl(var(--sb-ink))] dark:text-[hsl(40_60%_96%)]"
                    data-testid={`author-engagement-completed-chip-${readerId || name}`}
                  >
                    <Check size={11} strokeWidth={2.75} /> Engagement complete
                    {engagementCompletedAt && (
                      <> · {engagementCompletedAt.toLocaleDateString()}</>
                    )}
                  </span>
                )}
              </div>
              {dlAt && (
                <div
                  className="ml-1 flex items-center gap-1.5 text-[11px] text-muted-foreground"
                  data-testid={`author-manuscript-download-row-${readerId || name}`}
                >
                  <Download size={11} className="text-[hsl(var(--sb-forest-2))]" />
                  <span>
                    Manuscript downloaded · {dlAt.toLocaleString()}
                    {r.manuscript_download_count > 1 && (
                      <span> · {r.manuscript_download_count}× total</span>
                    )}
                  </span>
                </div>
              )}
              {isAuthor && applicationId && !engagementDone && (confState === "accepted" || confState === "update_required") && (
                <div className="ml-1 mt-1">
                  <button
                    type="button"
                    onClick={() => completeEngagement(applicationId, name)}
                    disabled={completing === applicationId}
                    className="btn-ghost border text-xs px-3 py-1.5"
                    data-testid={`author-engagement-complete-btn-${readerId || name}`}
                  >
                    {completing === applicationId ? "Marking…" : `Mark ${name}'s engagement complete`}
                  </button>
                </div>
              )}
              {isAuthor && applicationId && engagementDone && readerId && (
                <div className="ml-1 mt-1">
                  {r.author_review_submitted ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--sb-forest-2))]"
                      data-testid={`author-review-submitted-${readerId || name}`}
                    >
                      <Check size={13} /> Review submitted
                      {r.author_review_submitted_at && (
                        <span className="text-muted-foreground">
                          {" · "}
                          {new Date(r.author_review_submitted_at).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onOpenReview?.(readerId)}
                      className="btn-ghost border text-xs px-3 py-1.5"
                      data-testid={`author-review-btn-${readerId || name}`}
                    >
                      Review {name}
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {viewAgreementOpen && agreement && (
        <AuthorAgreementModal
          version={agreement.version}
          text={agreement.text}
          onClose={() => setViewAgreementOpen(false)}
        />
      )}
    </div>
  );
}

function ConfidentialityDeclineModal({
  reason, onReasonChange, confirmed, onConfirmedChange,
  submitting, onClose, onSubmit,
}) {
  const MAX = 500;
  // Body-scroll lock + Esc close, matching AuthorAgreementModal. Also
  // portaled into document.body so no transformed ancestor turns the
  // fixed overlay into a document-contained element on mobile.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, submitting]);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/35 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="manuscript-confidentiality-decline-modal"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] rounded-2xl border bg-background shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-border">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-destructive">
              Decline confidentiality
            </div>
            <h2 className="mt-1 font-serif text-2xl text-foreground">Decline this project?</h2>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="shrink-0 text-muted-foreground hover:text-foreground p-1 -mr-1"
            aria-label="Close"
            data-testid="manuscript-confidentiality-decline-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-5 space-y-4">
          <p className="text-sm leading-relaxed text-foreground">
            You&rsquo;re declining the confidentiality agreement for
            <strong className="font-semibold"> this project only</strong>. Your
            Last Bookmark account and other projects you&rsquo;re working on
            are unaffected.
          </p>
          <p className="text-sm leading-relaxed text-foreground">
            You will no longer be able to open the manuscript, and the
            author will be notified so they can accept another reader.
          </p>
          <div>
            <label
              htmlFor="decline-reason"
              className="block text-xs uppercase tracking-[0.14em] font-semibold text-foreground/80"
            >
              Reason (optional)
            </label>
            <textarea
              id="decline-reason"
              value={reason}
              onChange={(e) => onReasonChange(e.target.value.slice(0, MAX))}
              maxLength={MAX}
              rows={3}
              className="mt-1.5 w-full sb-input text-foreground placeholder:text-muted-foreground/80"
              placeholder="Help the author understand — not required."
              data-testid="manuscript-confidentiality-decline-reason"
              disabled={submitting}
            />
            <div className="mt-1 text-[11px] text-right text-foreground/60">
              {reason.length}/{MAX}
            </div>
          </div>
          <label className="flex items-start gap-2.5 text-sm cursor-pointer text-foreground">
            <input
              type="checkbox"
              className="mt-1"
              checked={confirmed}
              onChange={(e) => onConfirmedChange(e.target.checked)}
              data-testid="manuscript-confidentiality-decline-confirm"
              disabled={submitting}
            />
            <span>
              I understand this will cancel my engagement on this project and
              I will not be able to open the manuscript.
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 flex-wrap p-4 border-t border-border bg-background rounded-b-2xl">
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="btn-ghost border border-border text-foreground min-h-[44px] px-4"
            disabled={submitting}
            data-testid="manuscript-confidentiality-decline-cancel"
          >
            Keep this project
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!confirmed || submitting}
            className="btn-primary bg-destructive hover:bg-destructive/90 border-destructive min-h-[44px] px-5 disabled:opacity-60"
            data-testid="manuscript-confidentiality-decline-submit"
          >
            {submitting ? "Submitting…" : "Decline this project"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AuthorAgreementModal({ version, text, onClose }) {
  // Simple, robust modal. No compositor hints (no isolation, no
  // translateZ, no backdrop blur/filter, no opacity animations) because
  // those can cause ghost/paint trails on some desktop browsers when
  // the user moves the pointer rapidly or scrolls. The overlay itself
  // does not scroll — only the inner agreement body does — which keeps
  // the fixed layer completely stable. We also portal into document.body
  // so no transformed ancestor can turn the viewport-fixed overlay into
  // a document-contained element (which was pushing the panel below the
  // fold on small viewports).
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) {
      document.body.style.paddingRight = `${scrollbar}px`;
    }
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/35 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="author-confidentiality-agreement-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] rounded-2xl border bg-background shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-border">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[hsl(var(--sb-forest-2))]">
              <ShieldCheck size={18} />
              <span className="text-[11px] uppercase tracking-[0.14em] font-medium">
                Confidentiality agreement · v{version || "?"}
              </span>
            </div>
            <h2 className="mt-1 font-serif text-2xl">Agreement text</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              This is the exact wording readers accepted into your project are
              asked to sign before they can open the manuscript.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-muted-foreground hover:text-foreground p-1 -mr-1"
            aria-label="Close"
            data-testid="author-confidentiality-agreement-modal-close"
          >
            <X size={20} />
          </button>
        </div>
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-5"
          data-testid="author-confidentiality-agreement-body"
        >
          <div className="rounded-xl border border-border bg-card p-4 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
            {text || "(agreement text unavailable)"}
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-border bg-background rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="btn-primary min-h-[44px] px-5"
            data-testid="author-confidentiality-agreement-modal-close-footer"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}



function ManuscriptCard({ job, isAuthor, isAcceptedReader, onChanged, onOpenReview, engagementPanelKey }) {
  const [uploading, setUploading] = useState(false);
  const has = job.has_manuscript;
  const meta = job.manuscript;
  const hasUrl = !!job.has_manuscript_url;
  const url = job.manuscript_url || "";
  const closed = job.status === "completed" || job.status === "cancelled";

  // Inline link editor state — lets the author add / edit / clear the private
  // manuscript URL directly from the project page without navigating away.
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const openLinkEditor = (prefill) => {
    setLinkDraft(prefill || "");
    setLinkEditorOpen(true);
  };
  const cancelLinkEditor = () => {
    setLinkEditorOpen(false);
    setLinkDraft("");
  };
  const saveLink = async () => {
    const trimmed = (linkDraft || "").trim();
    setSavingLink(true);
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, { manuscript_url: trimmed });
      onChanged(data);
      toast.success(trimmed ? "Manuscript link saved" : "Manuscript link removed");
      setLinkEditorOpen(false);
      setLinkDraft("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingLink(false);
    }
  };

  // Confidentiality state — only relevant for accepted readers. Author + admin
  // never see the acknowledgment; they own the manuscript.
  const [ackStatus, setAckStatus] = useState(null); // {required, accepted, version, text, accepted_at}
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  // Phase B.3 — reader decline flow.
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declineConfirmed, setDeclineConfirmed] = useState(false);
  const [declineSubmitting, setDeclineSubmitting] = useState(false);
  useEffect(() => {
    if (!isAcceptedReader || isAuthor) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${job.id}/confidentiality`);
        if (alive) setAckStatus(data);
      } catch {
        if (alive) setAckStatus({ required: true, accepted: false });
      }
    })();
    return () => { alive = false; };
  }, [job.id, isAcceptedReader, isAuthor]);
  const needsAck = isAcceptedReader && !isAuthor && ackStatus && ackStatus.required && !ackStatus.accepted && ackStatus.state !== "declined";
  const hasDeclined = isAcceptedReader && !isAuthor && ackStatus && ackStatus.state === "declined";
  const hasAck = isAcceptedReader && (isAuthor || (ackStatus && (ackStatus.accepted || !ackStatus.required)));
  const isAckUpdate = needsAck && ackStatus?.state === "update_required";

  // Deep links (/jobs/:id#confidentiality) land on the agreement once it renders.
  useEffect(() => {
    if (!ackStatus || window.location.hash !== "#confidentiality") return;
    const t = setTimeout(() => {
      document.getElementById("confidentiality")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [ackStatus]);

  const acceptAck = async () => {
    setAckSubmitting(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/confidentiality/accept`, { agreed: true });
      setAckStatus({ ...(ackStatus || {}), ...data, required: true });
      // Re-fetch the job so the private URL (if any) is now included.
      try {
        const { data: fresh } = await api.get(`/jobs/${job.id}`);
        onChanged(fresh);
      } catch {}
      toast.success("Thanks — manuscript access unlocked.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAckSubmitting(false);
    }
  };

  const submitDecline = async () => {
    setDeclineSubmitting(true);
    try {
      await api.post(`/jobs/${job.id}/confidentiality/decline`, {
        confirm: true,
        reason: declineReason.trim() || undefined,
      });
      toast.success("Confidentiality declined. This project has been cancelled for you.");
      setDeclineOpen(false);
      // Flip the local ack status to declined so the UI immediately
      // swaps to the read-only "This engagement has been closed" panel —
      // without waiting for the user to reload the page. The authoritative
      // state is also re-fetched below from GET /jobs/{id}/confidentiality
      // so timestamps/version come from the server.
      setAckStatus((prev) => ({
        ...(prev || {}),
        required: true,
        accepted: false,
        state: "declined",
        declined_at: new Date().toISOString(),
      }));
      try {
        const { data: freshAck } = await api.get(`/jobs/${job.id}/confidentiality`);
        setAckStatus(freshAck);
      } catch {}
      // Re-fetch the job so manuscript visibility / status reflect the
      // cancelled engagement.
      try {
        const { data: fresh } = await api.get(`/jobs/${job.id}`);
        onChanged(fresh);
      } catch {}
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeclineSubmitting(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Manuscript must be ≤ 25 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/jobs/${job.id}/manuscript`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChanged(data);
      toast.success(meta ? "Manuscript replaced" : "Manuscript uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const download = async () => {
    try {
      // Use fetch to grab as blob (cookies auto-attach same-origin) then save
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/manuscript`, { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = meta?.filename || "manuscript";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the current manuscript?")) return;
    try { const { data } = await api.delete(`/jobs/${job.id}/manuscript`); onChanged(data); toast.success("Manuscript removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Author view — upload or replace/remove + private manuscript link display
  if (isAuthor) {
    return (
      <div className="card-soft" data-testid={TID.jobs.manuscriptCard}>
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-[hsl(var(--sb-forest-2))]" />
          <h2 className="font-serif text-2xl">Manuscript</h2>
        </div>
        <ConfidentialityStatusPanel
          jobId={job.id}
          isAuthor={isAuthor}
          refreshKey={engagementPanelKey}
          onEngagementCompleted={(data) => {
            if (data?.job) onChanged?.(data.job);
          }}
          onOpenReview={(readerId) => onOpenReview?.(readerId)}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          Upload the version your beta reader will read. Only you and the accepted reader can download it. PDF, DOC/DOCX, EPUB, TXT, RTF — up to 25 MB.
        </p>
        <AuthorManuscriptNotice className="mt-4" testId="author-manuscript-notice-project" />
        {has && meta && (
          <div className="mt-4 rounded-2xl border p-4 bg-muted/30">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{meta.filename}</div>
                <div className="text-xs text-muted-foreground">
                  {fmtSize(meta.size)} · {meta.content_type} · uploaded {new Date(meta.uploaded_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={download} data-testid={TID.jobs.manuscriptDownload} className="btn-ghost border">
                  <Download size={14} /> Download
                </button>
                {!closed && (
                  <button onClick={remove} data-testid={TID.jobs.manuscriptRemove} className="btn-ghost border text-destructive">
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {!closed && (
          <label className="mt-4 inline-flex items-center gap-2 cursor-pointer btn-primary">
            <Upload size={14} />
            {uploading ? "Uploading…" : (has ? "Replace manuscript" : "Upload manuscript")}
            <input type="file" hidden accept=".pdf,.doc,.docx,.epub,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip,text/plain,application/rtf"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid={has ? TID.jobs.manuscriptReplace : TID.jobs.manuscriptUpload} />
          </label>
        )}

        <div className="mt-5 pt-5 border-t">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">Private manuscript link</div>
          {url && !linkEditorOpen && (
            <>
              <div className="mt-2 flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0 flex items-center gap-2">
                  <ExternalLink size={14} className="text-[hsl(var(--sb-forest-2))] shrink-0" />
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm link-underline text-[hsl(var(--sb-forest-2))] break-all"
                    data-testid="job-manuscript-url-author"
                  >
                    {url}
                  </a>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary whitespace-nowrap"
                    data-testid="job-manuscript-url-open"
                  >
                    <ExternalLink size={14} /> Open manuscript link
                  </a>
                  <button
                    type="button"
                    onClick={() => openLinkEditor(url)}
                    className="btn-ghost border whitespace-nowrap"
                    data-testid="job-manuscript-url-edit"
                  >
                    Edit link
                  </button>
                </div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Only you and your accepted reader (after they accept the confidentiality acknowledgment) can see this link.
              </p>
            </>
          )}
          {!url && !linkEditorOpen && (
            <div className="mt-2 rounded-xl border-2 border-dashed p-4 flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-muted-foreground max-w-md">
                Prefer to work in Google Docs, Dropbox, OneDrive or another online document? Add a private link so your accepted reader can open it — without changing the file-upload option.
              </p>
              <button
                type="button"
                onClick={() => openLinkEditor("")}
                className="btn-ghost border whitespace-nowrap"
                data-testid="job-manuscript-url-add"
              >
                <ExternalLink size={14} /> Add manuscript link
              </button>
            </div>
          )}
          {linkEditorOpen && (
            <div
              className="mt-2 rounded-xl border p-4 bg-muted/30 space-y-3"
              data-testid="job-manuscript-url-editor"
            >
              <label className="block text-xs font-medium text-muted-foreground">
                Paste a shareable https:// link (Google Docs, Dropbox, OneDrive, etc.)
              </label>
              <input
                type="url"
                inputMode="url"
                autoFocus
                value={linkDraft}
                onChange={(e) => setLinkDraft(e.target.value)}
                placeholder="https://docs.google.com/document/d/…"
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sb-forest-2))]"
                data-testid="job-manuscript-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Make sure the link is set to “anyone with the link” or that you’ve shared it with your accepted reader. Only readers who’ve accepted the confidentiality acknowledgment will see it.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={saveLink}
                  disabled={savingLink}
                  className="btn-primary whitespace-nowrap"
                  data-testid="job-manuscript-url-save"
                >
                  {savingLink ? "Saving…" : (url ? "Save link" : "Add link")}
                </button>
                <button
                  type="button"
                  onClick={cancelLinkEditor}
                  disabled={savingLink}
                  className="btn-ghost border whitespace-nowrap"
                  data-testid="job-manuscript-url-cancel"
                >
                  Cancel
                </button>
                {url && (
                  <button
                    type="button"
                    onClick={() => { setLinkDraft(""); }}
                    disabled={savingLink}
                    className="btn-ghost border text-destructive whitespace-nowrap"
                    data-testid="job-manuscript-url-clear"
                  >
                    <Trash2 size={14} /> Clear link
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Accepted reader — confidentiality gate → celebratory access panel
  if (isAcceptedReader) {
    if (hasDeclined) {
      // Phase B.3 UI: once the reader has explicitly declined this
      // project's confidentiality agreement the engagement is closed. We
      // render a read-only state so the Accept/Decline controls can
      // never reappear and the manuscript stays locked. The rest of
      // Last Bookmark remains fully usable — this panel only replaces
      // the acknowledgment surface on this one project.
      const declinedAt = ackStatus?.declined_at ? new Date(ackStatus.declined_at) : null;
      return (
        <div
          id="confidentiality"
          className="card-soft border-2 border-destructive/40 scroll-mt-24"
          data-testid="manuscript-confidentiality-declined-panel"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full bg-destructive/15 text-destructive">
              <X size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-destructive">
                Confidentiality declined
              </div>
              <h2 className="font-serif text-2xl mt-0.5">This engagement has been closed</h2>
              {declinedAt && (
                <p
                  className="mt-1 text-xs text-muted-foreground"
                  data-testid="manuscript-confidentiality-declined-at"
                >
                  Declined on {declinedAt.toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed">
            You declined the confidentiality agreement for this project, so
            this engagement is now closed and the manuscript remains locked.
            The rest of Last Bookmark is unaffected — you can continue using
            your dashboard, apply to other projects, and message other
            authors as usual.
          </p>
          <div
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
            data-testid="manuscript-confidentiality-declined-lock"
          >
            <Lock size={12} /> Manuscript access remains locked
          </div>
        </div>
      );
    }
    if (needsAck) {
      return (
        <div
          id="confidentiality"
          className="card-soft border-2 border-[hsl(var(--sb-clay))]/50 scroll-mt-24"
          data-testid="manuscript-confidentiality-panel"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay-2))]">
              <ShieldCheck size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--sb-clay-2))]">
                {isAckUpdate ? `Agreement updated · v${ackStatus?.version}` : "One-time step"}
              </div>
              <h2 className="font-serif text-2xl mt-0.5">
                {isAckUpdate ? "Updated confidentiality agreement" : "Confidentiality acknowledgment"}
              </h2>
            </div>
          </div>
          {isAckUpdate && (
            <div className="mt-4 rounded-xl border border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 p-3 text-sm leading-relaxed" data-testid="manuscript-confidentiality-update-notice">
              The agreement now explicitly prohibits <strong>AI-generated beta feedback</strong> and
              <strong> sharing manuscript content with AI tools or services</strong>.
              {ackStatus?.previously_accepted_version && (
                <> You accepted v{ackStatus.previously_accepted_version}
                  {ackStatus?.accepted_at && <> on {new Date(ackStatus.accepted_at).toLocaleDateString()}</>}
                  {" "}— that record is kept.{" "}</>
              )}
              Manuscript access is paused until you accept v{ackStatus?.version}. Your application, engagement and messages are not affected.
            </div>
          )}
          <p className="mt-4 text-sm leading-relaxed whitespace-pre-line" data-testid="manuscript-confidentiality-text">
            {ackStatus?.text ||
              "I understand that manuscripts and other materials shared with me through Last Bookmark belong to the author and are provided solely for the purpose of completing this beta read. I agree not to copy, distribute, publish, upload, forward, sell, or otherwise share the author's manuscript or materials with anyone without the author's permission."}
          </p>
          <label className="mt-5 flex items-start gap-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={ackChecked}
              onChange={(e) => setAckChecked(e.target.checked)}
              data-testid="manuscript-confidentiality-checkbox"
            />
            <span>I agree to this confidentiality agreement, including reading the manuscript personally and not using AI tools or services with it.</span>
          </label>
          <div className="mt-5 flex justify-end items-center gap-3 flex-wrap">
            {!isAckUpdate && <button
              type="button"
              onClick={() => { setDeclineReason(""); setDeclineConfirmed(false); setDeclineOpen(true); }}
              disabled={ackSubmitting || declineSubmitting}
              className="btn-ghost border text-destructive"
              data-testid="manuscript-confidentiality-decline"
            >
              Decline this project
            </button>}
            <button
              onClick={acceptAck}
              disabled={!ackChecked || ackSubmitting}
              className="btn-primary"
              data-testid="manuscript-confidentiality-accept"
            >
              <ShieldCheck size={14} /> {ackSubmitting ? "Confirming…" : isAckUpdate ? `Accept v${ackStatus?.version}` : "Accept confidentiality"}
            </button>
          </div>
          {(has || hasUrl) && (
            <p className="mt-3 text-xs text-muted-foreground text-right">
              Manuscript access is currently <strong>locked</strong>.
              {has && hasUrl
                ? " Once you accept, you'll be able to download the file and open the private link below."
                : has
                  ? " Once you accept, you'll be able to download the manuscript file."
                  : " Once you accept, you'll be able to open the private manuscript link."}
            </p>
          )}
          {declineOpen && (
            <ConfidentialityDeclineModal
              reason={declineReason}
              onReasonChange={setDeclineReason}
              confirmed={declineConfirmed}
              onConfirmedChange={setDeclineConfirmed}
              submitting={declineSubmitting}
              onClose={() => setDeclineOpen(false)}
              onSubmit={submitDecline}
            />
          )}
        </div>
      );
    }
    return (
      <div
        id="confidentiality"
        className="card-soft border-2 border-[hsl(var(--sb-gold))]/70 shadow-md ring-1 ring-[hsl(var(--sb-gold))]/30 scroll-mt-24"
        data-testid={TID.jobs.manuscriptCard}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full bg-[hsl(var(--sb-gold))]/20 text-[hsl(var(--sb-gold-2))]">
            <CheckCircle2 size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--sb-gold-2))]">You&rsquo;re on this project</div>
            <h2 className="font-serif text-2xl mt-0.5">Manuscript access</h2>
            {(has && meta) || hasUrl ? (
              <p className="mt-1 text-sm">Everything below is private — for your eyes only.</p>
            ) : (
              <p className="mt-1 text-sm">The author hasn&rsquo;t shared the manuscript yet.</p>
            )}
          </div>
        </div>

        {has && meta && (
          <div className="mt-4 rounded-2xl border-2 border-[hsl(var(--sb-forest))]/40 p-4 bg-[hsl(var(--sb-cream))]/60">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <FileText size={22} className="text-[hsl(var(--sb-forest-2))] shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold truncate">{meta.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {fmtSize(meta.size)} · uploaded {new Date(meta.uploaded_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <button
                onClick={download}
                data-testid={TID.jobs.manuscriptDownload}
                className="btn-primary text-base px-5 py-3 shadow-sm"
              >
                <Download size={16} /> Download manuscript
              </button>
            </div>
          </div>
        )}

        {hasUrl && url && (
          <div className="mt-4 rounded-2xl border-2 border-[hsl(var(--sb-forest))]/40 p-4 bg-[hsl(var(--sb-cream))]/60">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <ExternalLink size={22} className="text-[hsl(var(--sb-forest-2))] shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold truncate">Private manuscript link</div>
                  <div className="text-xs text-muted-foreground break-all">{url}</div>
                </div>
              </div>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-base px-5 py-3 shadow-sm"
                data-testid="manuscript-open-link"
              >
                <ExternalLink size={16} /> Open manuscript link
              </a>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Please respect the author&rsquo;s sharing permissions — don&rsquo;t forward or share this link with anyone.
            </p>
          </div>
        )}

        {!has && !hasUrl && (
          <div className="mt-4 rounded-2xl border border-dashed p-4 bg-muted/30">
            <p className="text-sm">
              <strong>What to do next:</strong> message the author from the sidebar to check in and ask where they&rsquo;d like you to read
              (uploaded file, Google Docs link, or another platform). The manuscript will appear here as soon as they attach it.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Anyone else — informational only if the job has a manuscript / link.
  // NEVER expose the link itself here.
  if (!has && !hasUrl) return null;
  return (
    <div className="card-soft" data-testid={TID.jobs.manuscriptCard}>
      <div className="flex items-center gap-2">
        <FileText size={18} className="text-muted-foreground" />
        <h2 className="font-serif text-2xl">Manuscript attached</h2>
      </div>
      <p className="mt-2 text-sm text-muted-foreground" data-testid={TID.jobs.manuscriptGatedNote}>
        Only the accepted beta reader can access the manuscript. Apply and get accepted to unlock it.
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border p-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 text-sm font-medium capitalize">{value || "—"}</div>
    </div>
  );
}

/**
 * Compensation label for the project. Returns "Unpaid" when both bounds
 * are zero (previously rendered as "$0–$0"), otherwise the existing
 * "$min–$max" range. Only affects display — project data is unchanged.
 */
export function formatCompensation(job) {
  const min = Number(job?.budget_min ?? 0);
  const max = Number(job?.budget_max ?? 0);
  if (min === 0 && max === 0) return "Unpaid";
  return `$${job.budget_min}–$${job.budget_max}`;
}



function ApplicationRow({ app, jobStatus, onAccept, onDecline, onMessage }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const submittedOn = app.created_at ? new Date(app.created_at) : null;
  return (
    <div className="rounded-2xl border p-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          {app.reader_id ? (
            <Link
              to={`/profile/${app.reader_id}`}
              className="font-medium link-underline hover:text-[hsl(var(--sb-forest-2))]"
              data-testid={`application-reader-link-${app.id}`}
            >
              {app.reader_name}
            </Link>
          ) : (
            <div className="font-medium">{app.reader_name}</div>
          )}
          <div className="text-xs text-muted-foreground">
            Proposed: {app.proposed_rate ? `$${app.proposed_rate}` : "—"} · {app.proposed_turnaround_days ? `${app.proposed_turnaround_days} days` : "—"}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] rounded-full px-2.5 py-1 bg-muted text-muted-foreground">
          {app.status}
        </span>
      </div>
      <p className="mt-3 text-sm text-foreground/85 whitespace-pre-line leading-relaxed">{app.cover_letter}</p>
      {app.status === "pending" && jobStatus === "open" && (
        <div className="mt-4 flex gap-2 flex-wrap">
          <button onClick={onAccept} data-testid={TID.jobs.acceptApp} className="btn-primary">Accept</button>
          <button onClick={onDecline} data-testid={TID.jobs.declineApp} className="btn-ghost border">Decline</button>
          <button onClick={onMessage} className="btn-ghost border ml-auto" data-testid={`application-message-btn-${app.id}`}><MessageCircle size={14} /> Message</button>
        </div>
      )}
      {app.status === "accepted" && (
        <div className="mt-4 flex gap-2 flex-wrap">
          <button
            onClick={() => setDetailOpen(true)}
            className="btn-ghost border"
            data-testid={`application-view-${app.id}`}
          >
            <FileText size={14} /> View application
          </button>
          <button onClick={onMessage} className="btn-ghost border" data-testid={`application-message-btn-pending-${app.id}`}><MessageCircle size={14} /> Message</button>
        </div>
      )}
      {detailOpen && (
        <ApplicationDetailModal
          app={app}
          submittedOn={submittedOn}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

function ApplicationDetailModal({ app, submittedOn, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid={`application-detail-modal-${app.id}`}
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-card shadow-2xl border max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-3 border-b">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--sb-forest-2))] font-medium">Original application</div>
            <h3 className="mt-1 font-serif text-2xl truncate">{app.reader_name}</h3>
            {submittedOn && (
              <p className="mt-1 text-xs text-muted-foreground">Submitted {submittedOn.toLocaleString()}</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">Cover letter</div>
            <div className="rounded-xl border bg-muted/30 p-3.5 whitespace-pre-wrap text-sm leading-relaxed">
              {app.cover_letter || "(none)"}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Proposed rate</div>
              <div className="mt-1 font-medium">{app.proposed_rate ? `$${app.proposed_rate}` : "—"}</div>
            </div>
            <div className="rounded-xl border p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Turnaround</div>
              <div className="mt-1 font-medium">{app.proposed_turnaround_days ? `${app.proposed_turnaround_days} days` : "—"}</div>
            </div>
          </div>
          {app.reader_id && (
            <Link
              to={`/profile/${app.reader_id}`}
              className="btn-ghost border w-full justify-center"
              data-testid={`application-detail-open-profile-${app.id}`}
            >
              Open {app.reader_name}&rsquo;s profile
            </Link>
          )}
        </div>
        <div
          className="flex justify-end gap-2 px-6 py-4 border-t bg-card rounded-b-2xl"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <button type="button" onClick={onClose} className="btn-primary">Close</button>
        </div>
      </div>
    </div>
  );
}

function ApplyGuideCard({ signedIn }) {
  return (
    <div
      className="card-soft border-dashed border-2 border-[hsl(var(--sb-clay))]/60 bg-[hsl(var(--sb-cream))]"
      data-testid="jobs-apply-guide-card"
    >
      <div className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))] text-[11px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1">
        For beta readers
      </div>
      {signedIn ? (
        <>
          <h2 className="mt-4 font-serif text-2xl text-[hsl(150_32%_14%)]">Want to apply to this project?</h2>
          <p className="mt-2 text-sm text-[hsl(150_30%_20%)] leading-relaxed">
            Only beta readers can apply. Add the <span className="font-semibold text-[hsl(150_32%_14%)]">Beta Reader</span> role
            to your profile and the <span className="font-semibold text-[hsl(150_32%_14%)]">Apply to this project</span> button will appear here.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/profile"
              className="btn-primary"
              data-testid="jobs-apply-guide-profile-link"
            >
              Add Beta Reader role
            </Link>
            <Link
              to="/jobs"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border-2 border-[hsl(150_32%_14%)]/40 text-[hsl(150_32%_14%)] bg-transparent hover:bg-[hsl(150_32%_14%)]/5 text-sm font-medium transition-colors"
            >
              Browse other projects
            </Link>
          </div>
        </>
      ) : (
        <>
          <h2 className="mt-4 font-serif text-2xl text-[hsl(150_32%_14%)]">Want to apply to this project?</h2>
          <p className="mt-2 text-sm text-[hsl(150_30%_20%)] leading-relaxed">
            Beta readers can apply to projects directly. Create a free Last Bookmark
            account (or sign in) as a beta reader to send a cover letter.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/register"
              className="btn-primary"
              data-testid="jobs-apply-guide-register-link"
            >
              Sign up as a beta reader
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border-2 border-[hsl(150_32%_14%)]/40 text-[hsl(150_32%_14%)] bg-transparent hover:bg-[hsl(150_32%_14%)]/5 text-sm font-medium transition-colors"
            >
              I already have an account
            </Link>
          </div>
        </>
      )}
    </div>
  );
}


function ReaderPanel({ job, myApp, onApply, onEdit, onWithdraw, onMessage }) {
  if (!myApp) {
    if (job.status !== "open") {
      return <div className="card-soft"><p className="text-sm text-muted-foreground">This project isn't accepting applications right now.</p></div>;
    }
    if (job.applications_closed) {
      return <div className="card-soft" data-testid="reader-panel-apps-closed"><p className="text-sm text-muted-foreground">The author has paused new applications on this project. Check back later — they may reopen it.</p></div>;
    }
    return (
      <div className="card-soft">
        <h2 className="font-serif text-2xl">Interested?</h2>
        <p className="mt-2 text-sm text-muted-foreground">Send a short cover letter — authors read every one.</p>
        <button onClick={onApply} data-testid={TID.jobs.applyOpen} className="btn-primary mt-4">Apply to this project</button>
      </div>
    );
  }
  return (
    <div className="card-soft">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl">Your application</h2>
        <span
          className={
            myApp.status === "accepted"
              ? "text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2.5 py-1 border-2 border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))] text-[hsl(var(--sb-cream))] shadow-sm"
              : myApp.status === "pending"
              ? "text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2.5 py-1 border-2 border-[hsl(var(--sb-clay))] bg-[hsl(var(--sb-clay))] text-[hsl(var(--sb-cream))] shadow-sm"
              : "text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2.5 py-1 border-2 border-border bg-[hsl(var(--sb-ink))]/85 text-[hsl(var(--sb-cream))]"
          }
          data-testid={`reader-application-status-${myApp.status}`}
        >
          {myApp.status}
        </span>
      </div>
      <p className="mt-3 text-sm text-foreground/85 whitespace-pre-line leading-relaxed">{myApp.cover_letter}</p>
      {(myApp.proposed_rate != null || myApp.proposed_turnaround_days != null) && (
        <p className="mt-2 text-xs text-muted-foreground">
          {myApp.proposed_rate != null && <>Rate: ${myApp.proposed_rate}</>}
          {myApp.proposed_rate != null && myApp.proposed_turnaround_days != null && <> · </>}
          {myApp.proposed_turnaround_days != null && <>Turnaround: {myApp.proposed_turnaround_days} days</>}
        </p>
      )}
      <div className="mt-4 flex gap-2 flex-wrap">
        {myApp.status === "pending" && (
          <>
            <button onClick={onEdit} data-testid="jobs-edit-app" className="btn-primary">
              Edit Application
            </button>
            <button onClick={onWithdraw} data-testid={TID.jobs.withdrawApp} className="btn-ghost border">Withdraw</button>
          </>
        )}
        {myApp.status === "accepted" ? (
          <button onClick={onMessage} className="btn-ghost border" data-testid="jobs-reader-message-author">
            <MessageCircle size={14} /> Message the author
          </button>
        ) : (
          <p className="text-xs text-muted-foreground mt-1" data-testid="jobs-reader-messaging-hint">
            The author may message you here while your application is pending. If you'd like to start a conversation first, request a <span className="font-medium text-foreground">Coffee Chat</span> from their profile.
          </p>
        )}
      </div>
    </div>
  );
}

function ApplyDialog({ jobId, existing, unpaid = false, onClose, onDone }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    cover_letter: existing?.cover_letter || "",
    proposed_rate: existing?.proposed_rate ?? "",
    proposed_turnaround_days: existing?.proposed_turnaround_days ?? "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        cover_letter: form.cover_letter,
        // Unpaid projects never send a proposed rate — even if the user is
        // editing a legacy application that had one, the field is hidden
        // and we normalize to null so the persisted value clears.
        proposed_rate: unpaid ? null : (form.proposed_rate !== "" ? Number(form.proposed_rate) : null),
        proposed_turnaround_days: form.proposed_turnaround_days !== "" ? Number(form.proposed_turnaround_days) : null,
      };
      if (isEdit) {
        await api.patch(`/jobs/applications/${existing.id}`, body);
        toast.success("Application updated");
      } else {
        await api.post(`/jobs/${jobId}/apply`, body);
        toast.success("Application sent");
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4 overflow-y-auto"
      data-testid={isEdit ? "jobs-edit-app-dialog-backdrop" : "jobs-apply-dialog-backdrop"}
    >
      <form
        onSubmit={submit}
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-card shadow-2xl border sm:border max-h-[92vh] sm:max-h-[calc(100vh-4rem)] flex flex-col"
        data-testid={isEdit ? "jobs-edit-app-dialog" : "jobs-apply-dialog"}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-3 border-b bg-card rounded-t-2xl">
          <h3 className="font-serif text-2xl">{isEdit ? "Edit your application" : "Apply to project"}</h3>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4">
          {isEdit && (
            <p className="text-xs text-muted-foreground">
              You can revise this while your application is still pending. Once the author responds it will lock.
            </p>
          )}
          {!unpaid && (
            <div className="mt-4">
              <PlatformFeeNotice variant="real" compact />
            </div>
          )}
          <div className="mt-5 space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Cover letter</label>
              <textarea rows={6} required minLength={10} className="sb-input" placeholder="Why you'd love to read this book, what you bring…"
                value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                data-testid={TID.jobs.applyCoverLetter} />
            </div>
            <div className={unpaid ? "" : "grid grid-cols-2 gap-3"}>
              {!unpaid && (
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Proposed rate (optional)</label>
                  <input type="number" min={0} className="sb-input" value={form.proposed_rate}
                    onChange={(e) => setForm({ ...form, proposed_rate: e.target.value })}
                    data-testid={TID.jobs.applyRate} />
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Turnaround days (optional)</label>
                <input type="number" min={1} className="sb-input" value={form.proposed_turnaround_days}
                  onChange={(e) => setForm({ ...form, proposed_turnaround_days: e.target.value })}
                  data-testid={TID.jobs.applyTurnaround} />
              </div>
            </div>
          </div>
        </div>
        <div
          className="flex flex-col-reverse sm:flex-row justify-end gap-2 px-6 py-4 border-t bg-card rounded-b-2xl"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <button type="button" onClick={onClose} className="btn-ghost border w-full sm:w-auto">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary w-full sm:w-auto" data-testid={isEdit ? "jobs-edit-app-submit" : TID.jobs.applySubmit}>
            {saving ? (isEdit ? "Saving…" : "Sending…") : (isEdit ? "Save changes" : "Send application")}
          </button>
        </div>
      </form>
    </div>
  );
}


const VISIBILITY_OPTIONS = [
  {
    value: "public",
    label: "Public",
    hint: "All eligible beta readers can view and apply.",
  },
  {
    value: "private_testers",
    label: "Early Access Members Only",
    hint: "Only approved Early Access members and administrators can view and apply.",
  },
  {
    value: "default",
    label: "Private Draft",
    hint: "Keep private — only you and administrators can view it.",
  },
];

function VisibilityConfirmModal({ currentVisibility, choice, saving, onChoose, onCancel, onConfirm }) {
  const confirmDisabled = !choice || choice === currentVisibility || saving;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="job-visibility-modal">
      <div className="w-full max-w-lg rounded-2xl bg-card p-6 shadow-2xl border border-[hsl(var(--sb-forest))]/30">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl text-[hsl(var(--sb-forest-2))]">Change project visibility</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Select who can view and apply to this project. Your choice takes effect only after you confirm.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full p-2 hover:bg-muted disabled:opacity-40"
            aria-label="Close"
            data-testid="job-visibility-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-5 space-y-2">
          {VISIBILITY_OPTIONS.map((opt) => {
            const isCurrent = opt.value === currentVisibility;
            const selected = choice === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChoose(opt.value)}
                disabled={saving}
                data-testid={`job-visibility-option-${opt.value}`}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  selected
                    ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))]/10 ring-1 ring-[hsl(var(--sb-forest))]/40 !text-[hsl(var(--sb-ink))]"
                    : "border-border bg-[hsl(var(--card))] !text-[hsl(var(--sb-ink))] hover:bg-[hsl(var(--sb-cream-2))]"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold text-sm text-[hsl(var(--sb-ink))]">
                    {opt.label}
                    {isCurrent && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider !text-[hsl(var(--sb-ink))]/70">Current</span>
                    )}
                  </div>
                  {selected && <Check size={16} className="text-[hsl(var(--sb-forest-2))]" />}
                </div>
                <div className="text-xs !text-[hsl(var(--sb-ink))]/85 mt-1">{opt.hint}</div>
              </button>
            );
          })}
        </div>

        {choice && choice !== currentVisibility && (
          <div className="mt-4 rounded-md border border-[hsl(var(--sb-gold))]/50 bg-[hsl(var(--sb-gold))]/10 px-3 py-2 text-xs text-foreground/90" data-testid="job-visibility-modal-preview">
            {choice === "public" && (
              <>You're about to publish this project. Beta readers will be able to view details and apply.</>
            )}
            {choice === "private_testers" && (
              <>This project will be visible to approved Early Access members only.</>
            )}
            {choice === "default" && (
              <>This project will return to Private Draft — hidden from beta readers.</>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="btn-ghost border"
            data-testid="job-visibility-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="btn-primary disabled:opacity-50"
            data-testid="job-visibility-modal-confirm"
          >
            {saving ? "Saving…" : "Confirm change"}
          </button>
        </div>
      </div>
    </div>
  );
}
